import React, { Fragment, useCallback, useEffect, useState } from "react";
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from "react-router-dom";
import { debounce } from "lodash";
import Header from "./components/headerFooter/Header";
import Footer from "./components/headerFooter/Footer";
import Userlogin from "./pages/Login";
import Userregister from "./pages/Register";
import Resetpwd from "./pages/Resetpwd";
import Property from "./pages/Property";
import PropertyListing from "./pages/PropertyListing";
import ReferralPage from './pages/Referral';
import VerifyOtp from "./pages/VerifyOtp";
import VerifyOtpPassword from "./pages/VerifyOtpPassword";
import useThemeStore from "./store/themeStore";
import { getTheme, ThemeProps } from "./utils/theme";
import WhiteLabel from "./pages/WhiteLabel";
import Blog from "./pages/Blog";
import ScrollToTop from "./ScrollToTop";
import ServicesPage from "./pages/Services";
import PropertyValuation from "./pages/PropertyValuation";
import useTokenStore from "./store/store";
import "./App.css";
import { Helmet } from "react-helmet";
import { refreshSitemap } from './utils/sitemapGenerator';
import ClarityAnalytics from './components/ClarityAnalytics';
import { getClarityId } from './services/clarityService';

// SEO Component for Canonical URLs
const CanonicalUrl = ({ website }: { website?: string }) => {
  const location = useLocation();
  
  // Only generate canonical URLs for appropriate routes
  // Skip canonical for listing detail pages to prevent duplicate content
  const shouldHaveCanonical = !location.pathname.match(/^\/listing\/[^/]+/);
  
  if (!shouldHaveCanonical) {
    return null;
  }
  
  const canonicalUrl = website 
    ? `${website.replace(/\/$/, '')}${location.pathname}`
    : window.location.origin + location.pathname;
    
  return (
    <Helmet>
      <link rel="canonical" href={canonicalUrl} />
    </Helmet>
  );
};

// SEO Component for Common MetaTags
const CommonMetaTags = ({ theme }: { theme?: ThemeProps }) => {
  const location = useLocation();
  const isHomePage = location.pathname === "/";
  
  // Don't add meta tags to non-public routes
  const isPrivatePage = ['/login', '/register', '/resetPassword', '/verifyPasswordotp', '/verifyotp'].includes(location.pathname);
  
  if (isPrivatePage || !theme) {
    return null;
  }
  
  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <meta 
        name="description" 
        content={`${theme.name} offers professional property investment and portfolio building services. Find high-yielding buy-to-let properties sourced and vetted by ${theme.name}. Expert property sourcing and management solutions.`}
      />
      <meta 
        name="keywords" 
        content={`${theme.name}, property investment, buy-to-let properties, property portfolio, ${theme.website?.replace('https://', '').replace('www.', '')}, property sourcing, UK property investment`}
      />
      
      {/* Open Graph Tags */}
      <meta 
        property="og:title" 
        content={`${theme.name} | Build Your Property Investment Portfolio`}
      />
      <meta 
        property="og:description" 
        content={`Partner with ${theme.name} to discover premium property investment opportunities. Expert property sourcing and management services tailored to your investment goals.`}
      />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={theme.website || window.location.origin} />
      {theme.logo && <meta property="og:image" content={theme.logo} />}
      
      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={`${theme.name} | Property Investment Services`} />
      <meta 
        name="twitter:description" 
        content={`${theme.name} offers professional property investment services. Build your high-yielding property portfolio today.`}
      />
      {theme.logo && <meta name="twitter:image" content={theme.logo} />}
      
      {/* Structured Data - Only add on homepage */}
      {isHomePage && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": theme.name,
            "description": `${theme.name} provides professional property investment and portfolio building services in the UK.`,
            "url": theme.website || window.location.origin,
            "logo": theme.logo,
            "address": {
              "@type": "PostalAddress",
              "addressCountry": "GB"
            },
            "sameAs": theme.socialLinks || []
          })}
        </script>
      )}
    </Helmet>
  );
};

// Message Types
enum MessageType {
  THEME_UPDATE = 'THEME_UPDATE',
  COLOR_UPDATE = 'COLOR_UPDATE',
  THEME_UPDATE_RECEIVED = 'THEME_UPDATE_RECEIVED',
  COLOR_UPDATE_RECEIVED = 'COLOR_UPDATE_RECEIVED',
  COLOR_UPDATE_ERROR = 'COLOR_UPDATE_ERROR'
}

// Constants
const DEBOUNCE_MS = 300;

// Demo site defaults
const DEMO_DEFAULTS = {
  color1: '#1e1b4b',
  color2: '#4f46e5',
  txtColor: '#000000',
  logo: 'https://demo.whitelabelproperty.com/logo.svg',
  favIcon: 'https://demo.whitelabelproperty.com/favicon.ico'
};

// Allowed origins configuration
const ALLOWED_ORIGINS = [
  "http://localhost:3000",
  "http://localhost:3001",
  "https://main.dfcttlwz2z1ar.amplifyapp.com",
  "https://whitelabelproperty.com",
  "https://www.whitelabelproperty.com",
  "https://www.demo.whitelabelproperty.com",
  "https://demo.whitelabelproperty.com"
].map(origin => origin.replace(/\/$/, ''));

// Helper functions
const isEmpty = (value: any): boolean => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  return false;
};

const getThemeValue = (key: keyof typeof DEMO_DEFAULTS, value: any): any => {
  if (isEmpty(value) && key in DEMO_DEFAULTS) {
    return DEMO_DEFAULTS[key];
  }
  return value;
};

const normalizeOrigin = (origin: string): string => {
  return origin.replace(/\/$/, '').replace(/^(https?:\/\/)www\./, '$1');
};

const isAllowedOrigin = (origin: string): boolean => {
  const normalizedOrigin = normalizeOrigin(origin);
  return ALLOWED_ORIGINS.some(allowed => normalizeOrigin(allowed) === normalizedOrigin);
};

// Components
const ConditionalFooter = () => {
  const location = useLocation();
  const noFooterRoutes = ['/login', '/register', '/resetPassword', '/verifyPasswordotp', '/verifyotp'];
  return noFooterRoutes.includes(location.pathname) ? null : <Footer />;
};

const PrivateRoute = ({ element }: { element: JSX.Element }) => {
  const location = useLocation();
  const getToken = useTokenStore((state) => state.token);
  
  if (!getToken) {
    const intendedUrl = location.pathname + location.search;
    return (
      <Navigate
        to={`/login?redirectTo=${encodeURIComponent(intendedUrl)}`}
        replace
      />
    );
  }
  return element;
};

function App() {
  const setTheme = useThemeStore((state) => state.setTheme);
  const currentTheme = useThemeStore.getState().theme;
  const [loading, setLoading] = useState(true);

  // Add this effect to update the title as soon as possible
  useEffect(() => {
    const updateTitle = () => {
      const savedTheme = localStorage.getItem('whitelabel_theme');
      if (savedTheme) {
        try {
          const parsedTheme = JSON.parse(savedTheme);
          if (parsedTheme.name) {
            document.title = `${parsedTheme.name} | Property Investment Services`;
          }
        } catch (err) {
          console.error('Error parsing saved theme:', err);
        }
      }
    };
    
    // Try to set title from cached theme immediately
    updateTitle();
  }, []);

  // Update LoadingScreen component to show the website name if available
  const LoadingScreen = () => {
    const [dots, setDots] = useState('.');
    const theme = useThemeStore.getState().theme;
    
    useEffect(() => {
      const interval = setInterval(() => {
        setDots(prev => prev.length >= 3 ? '.' : prev + '.');
      }, 500);
      return () => clearInterval(interval);
    }, []);

    return (
      <div className="flex flex-col justify-center items-center h-screen bg-gray-50">
        <Helmet>
          <title>
            {theme?.name 
              ? `${theme.name} | Property Investment Services` 
              : 'Property Investment Platform | Loading...'}
          </title>
        </Helmet>
        <div className="w-16 h-16 mb-4">
          <img 
            src={theme?.logo || DEMO_DEFAULTS.logo} 
            alt="Loading"
            className="w-full h-full object-contain"
          />
        </div>
        <div className="text-xl font-medium text-gray-700">
          {theme?.name 
            ? `Loading ${theme.name}${dots}`
            : `Loading your property investment platform${dots}`}
        </div>
        <div className="mt-2 text-sm text-gray-500">
          Please wait while we personalize your experience
        </div>
      </div>
    );
  };

  // Load theme from localStorage
  const loadSavedTheme = useCallback(() => {
    try {
      const savedTheme = localStorage.getItem('whitelabel_theme');
      if (savedTheme) {
        const parsedTheme = JSON.parse(savedTheme);
        const themeWithDefaults = {
          ...parsedTheme,
          color1: getThemeValue('color1', parsedTheme.color1),
          color2: getThemeValue('color2', parsedTheme.color2),
          txtColor: getThemeValue('txtColor', parsedTheme.txtColor),
          logo: getThemeValue('logo', parsedTheme.logo),
          favIcon: getThemeValue('favIcon', parsedTheme.favIcon)
        };
        setTheme(themeWithDefaults);
        setLoading(false);
        console.debug('Loaded saved theme');
        return true;
      }
    } catch (err) {
      console.error('Error loading saved theme:', err);
    }
    return false;
  }, [setTheme]);

  // Fetch theme if not in localStorage
  const fetchTheme = useCallback(async () => {
    // Load from localStorage as a temporary state while fetching
    loadSavedTheme();
    
    try {
      // Always fetch from server
      const response = await getTheme();
      if (response) {
        const themeWithDefaults = {
          ...response,
          color1: getThemeValue('color1', response.color1),
          color2: getThemeValue('color2', response.color2),
          txtColor: getThemeValue('txtColor', response.txtColor),
          logo: getThemeValue('logo', response.logo),
          favIcon: getThemeValue('favIcon', response.favIcon)
        };
        setTheme(themeWithDefaults);
        // Update localStorage with fresh data
        localStorage.setItem('whitelabel_theme', JSON.stringify(themeWithDefaults));
      }
    } catch (err) {
      console.error('Error fetching theme:', err);
      // We already loaded from localStorage as fallback
    } finally {
      setLoading(false);
    }
  }, [setTheme, loadSavedTheme]);
  

  // Debounced color update sender
  // eslint-disable-next-line
  const debouncedSendColorUpdate = useCallback(
    debounce((colors: any) => {
      try {
        window.parent.postMessage({
          type: MessageType.COLOR_UPDATE,
          colors
        }, ALLOWED_ORIGINS[0]);
        console.debug('Color update sent');
      } catch (err) {
        console.error('Failed to send color update:', err);
      }
    }, DEBOUNCE_MS),
    []
  );

  // Handle incoming theme updates
  const handleMessage = useCallback((event: MessageEvent) => {
    if (!isAllowedOrigin(event.origin)) return;

    const { type, theme } = event.data;
    if (type === MessageType.THEME_UPDATE && theme) {
      try {
        const themeWithDefaults = {
          ...theme,
          color1: getThemeValue('color1', theme.color1),
          color2: getThemeValue('color2', theme.color2),
          txtColor: getThemeValue('txtColor', theme.txtColor),
          logo: getThemeValue('logo', theme.logo),
          favIcon: getThemeValue('favIcon', theme.favIcon)
        };
        
        setTheme(themeWithDefaults);
        localStorage.setItem('whitelabel_theme', JSON.stringify(themeWithDefaults));
        
        // Silent acknowledgment
        window.parent.postMessage({
          type: MessageType.THEME_UPDATE_RECEIVED,
          status: 'success'
        }, event.origin);
      } catch (err) {
        console.error('Error processing theme update:', err);
      }
    }
  }, [setTheme]);

  // Initialize theme and message listener
  useEffect(() => {
    fetchTheme();
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [fetchTheme, handleMessage]);

  // Watch for theme changes and send updates
  useEffect(() => {
    if (currentTheme?.color1 || currentTheme?.color2 || currentTheme?.txtColor) {
      const colors = {
        color1: currentTheme.color1 || DEMO_DEFAULTS.color1,
        color2: currentTheme.color2 || DEMO_DEFAULTS.color2,
        txtColor: currentTheme.txtColor || DEMO_DEFAULTS.txtColor
      };
      
      debouncedSendColorUpdate(colors);
      localStorage.setItem('whitelabel_theme', JSON.stringify({
        ...currentTheme,
        ...colors
        // eslint-disable-next-line
      }));
    }
    // eslint-disable-next-line
  }, [currentTheme?.color1, currentTheme?.color2, currentTheme?.txtColor, debouncedSendColorUpdate]);

  // Generate and update sitemap when theme changes
  useEffect(() => {
    if (currentTheme?.website) {
      // Generate sitemap content
      refreshSitemap(currentTheme);
      
      // Update robots.txt sitemap URL
      const robotsMeta = document.querySelector('meta[name="robots"]');
      if (!robotsMeta) {
        const meta = document.createElement('meta');
        meta.name = 'robots';
        meta.content = `index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1`;
        document.head.appendChild(meta);
      }
    }
    // eslint-disable-next-line 
  }, [currentTheme?.website]);

  const getThemes = useThemeStore(
    (state) => state.theme as unknown as ThemeProps
  );

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="App">
      <Router basename={"/"}>
        <ScrollToTop>
          <Fragment>
            {/* Global SEO Elements */}
            <Helmet>
              <title>
                {currentTheme?.name 
                  ? `${currentTheme.name} | Property Investment Services`
                  : 'Property Investment Platform'}
              </title>
              
              {/* Favicon */}
              {currentTheme?.favIcon && (
                <link rel="icon" href={currentTheme.favIcon} />
              )}
              
              {/* Sitemap link */}
              {currentTheme?.website && (
                <link 
                  rel="sitemap"
                  type="application/xml"
                  href={`${currentTheme.website}/sitemap.xml`}
                />
              )}
              
              {/* Mobile viewport */}
              <meta
                name="viewport"
                content="width=device-width, initial-scale=1, maximum-scale=1, viewport-fit=contain"
              />
              
              {/* Theme color */}
              <meta name="theme-color" content={currentTheme?.color1 || "#000000"} />
            </Helmet>
            
            {/* Add canonical URL */}
            <CanonicalUrl website={currentTheme?.website} />
            
            {/* Add common meta tags */}
            <CommonMetaTags theme={currentTheme} />
            
            {/* Main app content */}
            <Header />
            <ClarityAnalytics theme={currentTheme} />
            <Routes>
              <Route
                path="/"
                element={
                  getThemes && getThemes.hybrid ? (
                    <PropertyListing />
                  ) : (
                    <WhiteLabel />
                  )
                }
              />
              {/* Public Routes */}
              <Route path="/login" element={<Userlogin />} />
              <Route path="/register" element={<Userregister />} />
              <Route path="/resetPassword" element={<Resetpwd />} />
              <Route path="/verifyPasswordOtp" element={<VerifyOtpPassword />} />
              <Route path="/verifyotp" element={<VerifyOtp />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/services" element={<ServicesPage />} />
              <Route path="/propertyvaluation" element={<PropertyValuation />} />
              <Route path="/listing" element={<PropertyListing />} />
              <Route path="/Referral" element={<ReferralPage />} />
              {/* Private Routes */}
              <Route path="/listing/:id" element={<PrivateRoute element={<Property />} />} />
              <Route path="/listing/:id/failure" element={<PrivateRoute element={<Property />} />} />
              <Route path="/listing/:id/success" element={<PrivateRoute element={<Property />} />} />
            </Routes>
            <ConditionalFooter />
          </Fragment>
        </ScrollToTop>
        <ClarityAnalytics theme={currentTheme} />
      </Router>
    </div>
  );
}

export default App;